import { SeoHead } from '@/components/seo/SeoHead';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import '@/styles/bni.css';

/**
 * Link do vídeo de apresentação no YouTube (qualquer formato: watch, youtu.be, shorts, embed).
 * Vazio = a página mostra o aviso no lugar do player.
 */
const YOUTUBE_URL = 'https://youtu.be/G9LQ78k4FMo';

const WHATSAPP_URL =
  'https://wa.me/5516997787674?text=' +
  encodeURIComponent('Olá, Johnny! Vi sua apresentação no BNI e quero conversar sobre IA na minha empresa.');

function youtubeId(url: string): string | null {
  const match = url.match(/(?:youtu\.be\/|v=|\/embed\/|\/shorts\/|\/live\/)([A-Za-z0-9_-]{11})/);
  return match ? match[1] : null;
}

const ofertas = [
  {
    index: '01',
    title: 'Treinamento de IA para empresas',
    desc: '10 encontros ao vivo de 1 hora, para o time inteiro, cada pessoa aplicando no próprio trabalho desde o primeiro dia.',
    tags: ['Pedir bem', 'Dar contexto', 'Desconfiar', 'LGPD'],
  },
  {
    index: '02',
    title: 'Geniaria — plataforma de cursos',
    desc: 'Cursos próprios de desenvolvimento humano, a mesma plataforma com a marca da sua empresa (domínio, logo, login corporativo) e cursos feitos sob medida.',
    tags: ['Cursos próprios', 'Com a sua marca', 'Sob medida'],
  },
  {
    index: '03',
    title: 'Análise Cognitiva',
    desc: 'Um mapa de como a pessoa funciona — motores, perfil APEX, Roda da Vida e pontos cegos — para desenvolver gente e montar PDI. Não é avaliação psicológica.',
    tags: ['1ª análise grátis para empresas', 'Depois, R$ 99'],
  },
  {
    index: '04',
    title: 'Plano Forja',
    desc: 'A análise vira ação: um foco por ciclo, três comportamentos observáveis e revisão a cada 4 a 6 semanas, ao longo de 6 meses.',
    tags: ['Fogo', 'Origem', 'Rota', 'Jugo', 'Afiação'],
  },
];

const perfis = [
  { title: 'Dono ou diretor de empresa', desc: 'que já paga por IA e o time não usa — ou usa mal.' },
  { title: 'Gestor de RH ou T&D', desc: 'montando PDI e desenvolvimento de líderes.' },
  { title: 'Empresa ou escola', desc: 'que quer treinar a equipe numa plataforma com a própria marca.' },
];

const BniPage = () => {
  const videoId = youtubeId(YOUTUBE_URL);

  return (
    <>
      <SeoHead
        route="/bni"
        lang="pt-BR"
        title="Apresentação no BNI — Johnny Vaz"
        description="Treinamento de IA para empresas, a plataforma Geniaria, Análise Cognitiva e Plano Forja. A apresentação de Johnny Vaz, líder de IA na PH3A, para o BNI."
        image="https://johnnyvaz.com.br/images/og/palestrante-og.png"
      />
      <Navbar />
      <main id="bni-content" className="pulse-page" tabIndex={-1}>
        <section className="bni-wrap bni-hero" aria-labelledby="bni-title">
          <p className="pulse-eyebrow">Apresentação para o BNI · Johnny Vaz</p>
          <h1 id="bni-title">
            Inteligência não se baixa. <span>Se forma.</span>
          </h1>
          <p className="bni-lead">
            Sou engenheiro de software, líder de inteligência artificial na PH3A e fundador da Geniaria.
            Formo times para usar IA com critério e ajudo empresas a desenvolver pessoas. Em menos de dois
            minutos, o vídeo mostra o que eu faço e como você pode me indicar.
          </p>
          <div className="bni-video">
            {videoId ? (
              <iframe
                src={`https://www.youtube-nocookie.com/embed/${videoId}?rel=0`}
                title="Apresentação de Johnny Vaz no BNI"
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
            ) : (
              <p className="bni-video-vazio">O vídeo da apresentação entra aqui em breve.</p>
            )}
          </div>
        </section>

        <section id="o-que-faco" className="pulse-section" aria-labelledby="bni-ofertas-title">
          <div className="bni-wrap">
            <p className="pulse-eyebrow">O que eu faço</p>
            <h2 id="bni-ofertas-title" style={{ marginBottom: '2.5rem' }}>
              IA com critério, para times e pessoas.
            </h2>
            <div className="bni-ph3a" style={{ marginBottom: '2.5rem' }}>
              <p className="bni-ph3a-num">~60</p>
              <div>
                <h3>Líder de IA na PH3A</h3>
                <p>
                  Lidero os projetos de IA em análise de dados e automação de processos, e conduzo o
                  treinamento de IA da empresa: cerca de 60 pessoas, do financeiro ao suporte, a maioria
                  sem nunca ter aberto uma IA.
                </p>
              </div>
            </div>
            <div className="bni-cards">
              {ofertas.map((oferta) => (
                <article key={oferta.index} className="bni-card">
                  <p className="bni-card-index">{oferta.index}</p>
                  <h3>{oferta.title}</h3>
                  <p>{oferta.desc}</p>
                  <ul>
                    {oferta.tags.map((tag) => (
                      <li key={tag}>{tag}</li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="referencia" className="pulse-section" aria-labelledby="bni-ref-title">
          <div className="bni-wrap">
            <p className="pulse-eyebrow">Como você pode me ajudar</p>
            <h2 id="bni-ref-title" style={{ marginBottom: '2.5rem' }}>
              “Meu time não sabe usar IA direito.”
            </h2>
            <p className="bni-lead" style={{ marginBottom: '2.5rem' }}>
              Ouviu essa frase? Me apresente. O diagnóstico de 30 minutos é por minha conta. Uma boa
              referência para mim é:
            </p>
            <ul className="bni-perfis">
              {perfis.map((perfil) => (
                <li key={perfil.title}>
                  <strong>{perfil.title}</strong>
                  <span>{perfil.desc}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section id="contato" className="pulse-section bni-convite" aria-labelledby="bni-contato-title">
          <div className="bni-wrap">
            <p className="pulse-eyebrow">Diagnóstico de 30 minutos, sem custo</p>
            <h2 id="bni-contato-title">
              Vamos <span>conversar.</span>
            </h2>
            <p className="bni-convite-lead">
              Me chame no WhatsApp e conte o momento da sua empresa — ou de quem você quer me apresentar.
            </p>
            <div className="bni-convite-acoes">
              <a className="pulse-button pulse-button-dark" href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                Falar no WhatsApp · (16) 99778-7674 <span aria-hidden="true">↗</span>
              </a>
            </div>
            <p className="bni-convite-links">
              <a href="https://geniaria.com.br" target="_blank" rel="noopener noreferrer">geniaria.com.br</a>
              <a href="/palestras">Palestras e treinamento in-company</a>
              <a href="https://www.linkedin.com/in/johnnyvaz/" target="_blank" rel="noopener noreferrer">LinkedIn</a>
            </p>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default BniPage;
